import { DataTableSort } from 'modules/Ui/DataTable/DataTable.models';
import { Currency } from 'types/currency';

import { DigitalDocumentStatus, DocumentStatus } from './document';
import { StatusInfoUI } from './statusInfo';
import { VerifactuTaxAuthority } from './verifactuTaxAuthority';

export interface VerifactuDocument {
  id?: string;
  fullInvoiceNumber?: string;
  code?: string;
  seriesCode?: string;
  taxRegime: string;
  operationDescription: string;
  operationDate?: string;
  issuedDate?: string;
  issuedAt?: string;
  updatedAt?: string;
  lines: VerifactuLine[];
  taxLines: VerifactuTaxLine[];
  reimbursableExpenseLines?: VerifactuReimbursableExpenseLine[]; // TODO: Definir tipo específico si es necesario
  currency: string;
  totals: VerifactuTotals;
  comments?: string;
  dueDate?: string;
  reference?: string;
  displaySettings: VerifactuDisplaySettings;
  counterparty?: VerifactuCounterparty;
  frozenCounterparty?: VerifactuFrozenCounterparty;
  series: VerifactuSeries;
  status?: string;
  type?: VerifactuInvoiceType;
  acceptedPaymentMethods?: VerifactuPaymentMethod[];
}

export enum VerifactuInvoiceType {
  Standard = 'STANDARD',
  Receipt = 'RECEIPT',
  Test = 'TEST',
}

export interface VerifactuLine {
  discountRate: number;
  discountAmount: number;
  title: string;
  description: string;
  quantity: number;
  unitPrice: number;
  totalAmount: number;
  taxes: VerifactuTaxLine;
  product?: VerifactuProduct;
  productReference?: string;
}

export interface VerifactuProduct {
  id: string;
}

export interface VerifactuTaxLine {
  taxType: string;
  nonTaxableTaxType?: string;
  nonTaxableCause?: NonTaxableCause | null;
  taxRate: number;
  taxBase: number;
  taxAmount: number;
  equivalenceSurchargeRate?: number;
  equivalenceSurchargeAmount?: number;
}

export interface VerifactuReimbursableExpenseLine {
  amount: number;
  description: string;
  id: string;
}

export interface VerifactuTotals {
  subTotalAmount: number;
  discountAmount: number;
  discountPercentage: number;
  amountDue: number;
  paidAmount: number;
  totalAmount: number;
  reimbursableExpensesAmount: number;
  withholdingRate: number;
  withholdingAmount: number;
  taxBase: number;
  taxAmount: number;
}

export interface VerifactuDisplaySettings {
  lines: VerifactuDisplayLine[];
  showReference: boolean;
  showLinesWithoutTaxes: boolean;
  template: VerifactuTemplate;
}

export interface VerifactuDisplayLine {
  type: string;
  text: string;
  order: number;
  groupName: string;
}

export interface VerifactuTemplate {
  id: string;
}

export interface VerifactuCounterparty {
  id: string;
}

export interface VerifactuFrozenCounterparty {
  id?: string;
  fiscalName: string;
  vatNumber: string;
  address: VerifactuAddress;
  vatNumberType: VerifactuVatNumberType;
}

export enum VerifactuVatNumberType {
  NIF = 'NIF',
  NIF_IVA = 'NIF-IVA',
  PASSPORT = 'PASSPORT',
  ORIGIN = 'ORIGIN',
  CERTIFICATE = 'CERTIFICATE',
  OTHER = 'OTHER',
}

export interface VerifactuAddress {
  country: string;
  postalAddress: string;
  postalCode: string;
  city: string;
  province: string;
}

export interface VerifactuSeries {
  id: string;
}
export interface VerifactuDatatable extends VerifactuInvoice {
  warningSelectable?: boolean;
}

export type VerifactuDocumentSort = DataTableSort<
  VerifactuDatatable,
  keyof VerifactuDatatable
>;

export interface VerifactuFilters {
  searchTerm?: string;
  issuedDateFrom?: string;
  minAmount?: number | string;
  maxAmount?: number | string;
  issuedDateTo?: string;
  statusInfo?: StatusInfoUI;
  dueDate?: string;
  digitalDocumentStatus?: DigitalDocumentStatus;
}
export interface VerifactuPaymentMethod {
  id: string;
}

export type NonTaxableCause = ExemptCause | NotSubjectCause;

export enum ExemptCause {
  E1_Article20 = 'E1',
  E2_Article21 = 'E2',
  E3_Article22 = 'E3',
  E4_Articles23And24 = 'E4',
  E5_Article25 = 'E5',
  E6_Other = 'E6',
}

export enum NotSubjectCause {
  N1_Article7_14_Others = 'N1',
  N2_LocationRules = 'N2',
}

export interface VerifactuInvoice extends VerifactuDocument {
  id: string;
  invoiceNumber: number;
  fullInvoiceNumber: string;
  status: DocumentStatus;
  frozenCounterparty: VerifactuFrozenCounterparty;
  seriesCode: string;
  currency: Currency;
  issuedDate: string;
  issuedAt: string;
  type: VerifactuInvoiceType;
  taxAuthority: VerifactuTaxAuthority;
  frozenOwner: VerifactuFrozenOwner;
}

export interface VerifactuFrozenOwner extends VerifactuFrozenCounterparty {}
